import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Settings,
  LogOut,
  LogIn,
  ChevronDown,
  UserCircle2,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '@kss/auth';
import { cn } from '../../utils/cn';

/**
 * Single dropdown shared by the panel topbar AND the landing TopNav.
 *
 *   Anonymous visitor:
 *     ┌───────────┐
 *     │ ⨀  ⌄ Sign in │
 *     └───────────┘
 *   Signed-in user:
 *     ┌─────────────────────────┐
 *     │ ⨀  Aditi Sharma ⌄        │
 *     └─────────────────────────┘
 *       opens                                               ⌄
 *     ┌─────────────────────────┐
 *     │ Aditi Sharma             │
 *     │ aditi@statecorp.com      │
 *     │ [SuperAdmin]              │
 *     ├─────────────────────────┤
 *     │ 👤 My profile            │ → /panel/profile
 *     │ ⚙ Settings               │ → /panel/settings
 *     ├─────────────────────────┤
 *     │ ↪ Sign out               │ red, dispatches logout()
 *     └─────────────────────────┘
 *
 * Reads `state.auth.session / state.auth.user` from the Redux store.
 * No props are required; pass `compact` to hide the name+chevron next
 * to the avatar (used on narrow viewports).
 */

interface AuthState {
  session: { sessionId?: string } | null;
  user: { id?: string; name?: string; email?: string; role?: string } | null;
  isAuthenticated: boolean;
}
type RootWithAuth = { auth: AuthState };

export interface UserProfileDropdownProps {
  /** Hide the name + chevron next to the avatar. Defaults to false. */
  compact?: boolean;
  /** Override profile-link target. */
  profileHref?: string;
  /** Override settings-link target. */
  settingsHref?: string;
  /** Override sign-in target (anonymous visitors). */
  signInHref?: string;
  /** Where to send the user after a successful sign-out. */
  postLogoutHref?: string;
}

export default function UserProfileDropdown({
  compact = false,
  profileHref = '/panel/profile',
  settingsHref = '/panel/settings',
  signInHref = '/login',
  postLogoutHref = '/',
}: UserProfileDropdownProps = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Read live auth state — no mock data.
  const isAuthenticated = useSelector((s: RootWithAuth) => !!s.auth?.isAuthenticated);
  const user = useSelector((s: RootWithAuth) => s.auth?.user) ?? null;
  const displayName = user?.name?.trim() || (isAuthenticated ? 'Signed-in user' : 'Account');
  const email = user?.email?.trim() || '';
  const role = user?.role?.trim() || '';
  const initial = (displayName || '?').slice(0, 1).toUpperCase();

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  function handleLogout() {
    dispatch(logout());
    setOpen(false);
    navigate(postLogoutHref);
  }

  return (
    <div ref={ref} className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={isAuthenticated ? displayName : 'Sign in'}
        className={cn(
          'flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors',
          'hover:bg-surface-card-hover',
        )}
      >
        {isAuthenticated ? (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {initial}
          </span>
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-full text-content-secondary">
            <UserCircle2 size={22} />
          </span>
        )}
        {!compact && (
          <>
            {/* Show the user's name only when signed in — anonymous
                visitors see icon + chevron only (no "Sign in" label). */}
            {isAuthenticated && (
              <span className="hidden max-w-[140px] truncate text-sm font-medium text-content-primary md:inline">
                {displayName}
              </span>
            )}
            <ChevronDown size={14} className="hidden text-content-muted md:inline" />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-surface-card shadow-lg"
        >
          {isAuthenticated ? (
            <>
              <div className="border-b border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {initial}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-content-primary">{displayName}</p>
                    {email && <p className="truncate text-xs text-content-muted">{email}</p>}
                  </div>
                </div>
                {role && (
                  <span className="mt-2 inline-block rounded-full bg-primary-light px-2 py-0.5 text-[10px] font-semibold text-primary">
                    {role}
                  </span>
                )}
              </div>
              <div className="py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go(profileHref)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
                >
                  <User size={16} /> My profile
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go(settingsHref)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
                >
                  <Settings size={16} /> Settings
                </button>
              </div>
              <div className="border-t border-border py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-danger transition-colors hover:bg-danger-light"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="border-b border-border px-4 py-3">
                <p className="text-sm font-semibold text-content-primary">You're not signed in</p>
                <p className="mt-0.5 text-xs text-content-muted">
                  Sign in to access the dashboard, payslips, leave applications, and more.
                </p>
              </div>
              <div className="py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go(signInHref)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-primary transition-colors hover:bg-surface-card-hover"
                >
                  <LogIn size={16} /> Sign in
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go(signInHref)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
                >
                  <User size={16} /> Create an account
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
